import React, { useState } from 'react';

import { Select } from '@aline/common';
import { DialogActions, DialogContent, DialogContentText } from '@mui/material';
import { EditableNameInput } from 'src/components/EditableNameInput';
import { AlineButton, AlineDialog, SmallCloseIconButton, Spacer } from 'src/components/generic';
import { UploadCloudIcon } from 'src/components/icons/UploadCloudIcon';
import { StorableIcon } from 'src/components/StorableIcon';
import { AddWorkflowFilesButton } from 'src/components/workflow';
import { Apollo } from 'src/gql/generated';
import { useGetWorkflowFiles } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

import { StepLayout } from './StepLayout';

const useStyles = makeStyles()(theme => ({
  uploadDocsArea: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    height: '100%',
    justifyContent: 'center',
    margin: 24,
    textAlign: 'center',
  },
  uploadDocsBody: {
    border: '1px dashed #D1D1D1',
    borderRadius: 3,
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1),
    height: '100%',
    justifyContent: 'center',
    textAlign: 'center',
  },
  uploadSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
}));

export function AddDocumentsStep({ workflowId }: { workflowId: UUID }): JSX.Element {
  const { classes } = useStyles();

  return (
    <StepLayout n={2} title="Add documents">
      <div className={classes.uploadSection}>
        <div className={classes.uploadDocsBody}>
          <div className={classes.uploadDocsArea}>
            <UploadCloudIcon />
            <AddWorkflowFilesButton
              dataCy={Select.sendForSignature.addWorkflowFiles.button}
              uploadItemDataCy={Select.sendForSignature.addWorkflowFiles.uploadFileMenuItem}
              uploadPickerDataCy={Select.sendForSignature.addWorkflowFiles.uploadFilePicker}
              workflowId={workflowId}
            />
          </div>
        </div>
        <DocumentList workflowId={workflowId} />
      </div>
    </StepLayout>
  );
}

const useDocumentListStyles = makeStyles()(theme => ({
  docListItem: {
    alignItems: 'center',
    border: '1px solid #D7D7D7',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1),
    justifyContent: 'space-between',
    padding: theme.spacing(1),
  },
  docListVert: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  docNameInputBase: {
    fontSize: 14,
    fontWeight: 'normal',
    minWidth: 320,
  },
  docTitle: {
    overflow: 'hidden',
  },
}));

function DocumentList({ workflowId }: { workflowId: UUID }): JSX.Element | null {
  const { classes } = useDocumentListStyles();
  const { workflowFiles } = useGetWorkflowFiles(workflowId);
  const [updateDocumentProps] = Apollo.useUpdateDocumentPropsMutation();
  const [deleteDocumentId, setDeleteDocumentId] = useState<UUID | null>(null);

  if (!workflowFiles.length) return null;

  return (
    <div className={classes.docListVert}>
      {workflowFiles.map(file => {
        return (
          <div
            key={file.document.id}
            className={classes.docListItem}
            data-cy={Select.sendForSignature.addWorkflowFiles.fileRow}
          >
            <StorableIcon iconProps={{ height: 33, width: 33 }} storable={file.document} />
            <div className={classes.docTitle}>
              <EditableNameInput
                fullWidth={false}
                inputBaseClassName={classes.docNameInputBase}
                name={file.document.name}
                updateName={async name => {
                  await updateDocumentProps({
                    variables: { input: { documentId: file.document.id, documentProps: { name } } },
                  });
                }}
              />
            </div>
            <Spacer />
            <SmallCloseIconButton onClick={() => setDeleteDocumentId(file.document.id)} />
          </div>
        );
      })}
      {deleteDocumentId && (
        <RemoveDocDialog
          documentId={deleteDocumentId}
          open={Boolean(deleteDocumentId)}
          workflowId={workflowId}
          onClose={() => setDeleteDocumentId(null)}
        />
      )}
    </div>
  );
}

export function RemoveDocDialog({
  documentId,
  onClose,
  open,
  workflowId,
}: {
  documentId: UUID;
  onClose: () => void;
  open: boolean;
  workflowId: UUID;
}): JSX.Element {
  const [movingToTrash, setMovingToTrash] = useState(false);
  const [deletingFromWorkflow, setDeletingFromWorkflow] = useState(false);
  const [removeDocumentFromWorkflow] = Apollo.useRemoveDocumentsFromWorkflowMutation();
  const [disableDocument] = Apollo.useDisableDocumentsMutation();

  async function deleteFromWorkflow() {
    setDeletingFromWorkflow(true);
    // Variables provided at hook level
    await removeDocumentFromWorkflow({
      variables: { input: { documentIds: [documentId], workflowId } },
    });
    setDeletingFromWorkflow(false);
    onClose();
  }

  async function moveToTrashAndRemoveFromWorkflow() {
    setMovingToTrash(true);
    // Variables provided at hook level
    await removeDocumentFromWorkflow({
      variables: { input: { documentIds: [documentId], workflowId } },
    });
    await disableDocument({ variables: { input: { documentIds: [documentId] } } });
    setMovingToTrash(false);
    onClose();
  }

  return (
    <AlineDialog open={open} title="Remove document?" onClose={onClose}>
      <DialogContent>
        <DialogContentText>
          Should the document also be deleted from the repository?
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <AlineButton
          color="primary"
          disabled={movingToTrash}
          loading={deletingFromWorkflow}
          variant="outlined"
          onClick={deleteFromWorkflow}
        >
          Delete from Workflow
        </AlineButton>
        <AlineButton
          color="primary"
          disabled={deletingFromWorkflow}
          loading={movingToTrash}
          type="submit"
          variant="contained"
          onClick={moveToTrashAndRemoveFromWorkflow}
        >
          Move to Trash
        </AlineButton>
      </DialogActions>
    </AlineDialog>
  );
}
